import type React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { faFolderOpen } from '@fortawesome/free-regular-svg-icons';
import { BasePage } from '../BasePage';
import { ErrorPage } from '../ErrorPage';
import { LoadingPage } from '../LoadingPage';
import { buildEmptyProjectSummary } from './graphql/projectSummary';
import { EmptyState } from '../../emptyState/EmptyState';
import { CreateProjectMenu } from './components/CreateProjectMenu/CreateProjectMenu';
import { ExpandedProjectMeasuringPoints } from './components/ExpandedProjectMeasuringPoints';
import { useStandaloneMeasuringPointsQuery } from './graphql/useStandaloneMeasuringPointsQuery';
import { useProjectCatalogQuery } from './graphql/useProjectCatalogQuery';
import {
    getProjectDetailsId,
    getProjectRowKey,
    useExpandedProjectRows,
} from './hooks/useExpandedProjectRows';
import { useLoadExpandedMeasuringPoints } from './hooks/useLoadExpandedMeasuringPoints';
import { useOverviewTabState } from './hooks/useOverviewTabState';
import type { ProjectOverviewRow } from './types';
import { createProjectCatalogColumns } from './table/projectCatalogColumns';
import { OverviewCatalogSection } from './sections/OverviewCatalogSection';
import { OverviewStandaloneSection } from './sections/OverviewStandaloneSection';
import { OverviewToolbar } from './sections/OverviewToolbar';
import { hasAtLeastPermission, PERMISSION } from '../sharing/utils';
import { useProfile } from '../../../profile';

const EMPTY_STATE_VARIANT = 'brand';
const OVERVIEW_EMPTY_STATE_ICON = faFolderOpen;

export function OverviewPage() {
    const { canAccessReportBuilderProjectTemplates } = useProfile();
    const {
        selectedTab,
        isArchivedTab,
        isStandaloneTab,
        setSelectedTab,
        setArchiveFilter,
        searchTerm,
        setSearchTerm,
        debouncedSearchTerm,
        orderAndPaginationControl,
    } = useOverviewTabState();

    const { pageCount } = orderAndPaginationControl;

    const {
        loading,
        updating,
        error,
        starredProjects,
        projectRows,
        projectSummariesByProjectId,
        hasAnyData,
    } = useProjectCatalogQuery({
        archived: isArchivedTab,
        search: debouncedSearchTerm,
        orderAndPaginationControl,
        enabled: !isStandaloneTab,
    });

    const {
        loading: unassignedLoading,
        updating: unassignedUpdating,
        error: unassignedError,
        measuringPoints: unassignedMeasuringPoints,
    } = useStandaloneMeasuringPointsQuery({
        archived: isArchivedTab,
        search: debouncedSearchTerm,
        orderAndPaginationControl,
        enabled: isStandaloneTab,
    });

    const { expandAll, setExpandAll, setExpandedProjects, isProjectExpanded, toggleProjectRow } =
        useExpandedProjectRows({ projectRows });

    const { failedProjectIds } = useLoadExpandedMeasuringPoints({
        projectRows,
        isProjectExpanded,
        archived: isArchivedTab,
        paused: updating,
    });

    // Automatically expands the first result when a user performs a search.
    // With "expand all" on, every result is expanded already.
    useEffect(() => {
        if (
            expandAll ||
            isStandaloneTab ||
            !debouncedSearchTerm.trim() ||
            projectRows.length === 0
        ) {
            return;
        }

        const firstProjectRowKey = getProjectRowKey(projectRows[0]);

        setExpandedProjects((current) => {
            // Prevent re-render if the first row is already the only one expanded.
            if (current.size === 1 && current.has(firstProjectRowKey)) {
                return current;
            }

            return new Set([firstProjectRowKey]);
        });
    }, [debouncedSearchTerm, expandAll, isStandaloneTab, projectRows, setExpandedProjects]);

    const columns = useMemo(
        () =>
            createProjectCatalogColumns({
                getIsRowExpanded: isProjectExpanded,
                onToggleExpand: toggleProjectRow,
                expandAll,
                onExpandAllChange: setExpandAll,
                canShowProjectOptions: (project) =>
                    hasAtLeastPermission(project.permission, PERMISSION.View) &&
                    (!isArchivedTab || project.archived),
                showProjectAttributes: canAccessReportBuilderProjectTemplates,
                isArchivedView: isArchivedTab,
                starredProjects,
                getProjectSummary: (project) =>
                    isArchivedTab
                        ? buildEmptyProjectSummary()
                        : projectSummariesByProjectId[project.projectId] ?? null,
            }),
        [
            canAccessReportBuilderProjectTemplates,
            starredProjects,
            isProjectExpanded,
            expandAll,
            setExpandAll,
            isArchivedTab,
            projectSummariesByProjectId,
            toggleProjectRow,
        ]
    );

    const hasFilteredProjects = projectRows.length > 0;
    const hasFilteredUnassignedMeasuringPoints = unassignedMeasuringPoints.length > 0;

    const renderExpandedRows = useCallback(
        (projectRow: ProjectOverviewRow) => {
            if (!isProjectExpanded(projectRow)) {
                return null;
            }

            return (
                <ExpandedProjectMeasuringPoints
                    projectRow={projectRow}
                    archived={isArchivedTab}
                    detailsId={getProjectDetailsId(projectRow)}
                    loadFailed={failedProjectIds.has(projectRow.projectId)}
                />
            );
        },
        [failedProjectIds, isArchivedTab, isProjectExpanded]
    );

    const pageLoading = loading || unassignedLoading;
    const isUnassignedTableLoading = unassignedLoading || unassignedUpdating;
    const showUnassignedTableSection =
        hasFilteredUnassignedMeasuringPoints || isUnassignedTableLoading;
    const pageUpdating = isStandaloneTab ? unassignedUpdating : updating;
    const showProjectTableSection = hasFilteredProjects || pageUpdating;
    const pageError = isStandaloneTab ? unassignedError ?? error : error;
    const shouldShowCreateProjectAction = !isStandaloneTab;

    function renderOverviewEmptyState(header: string, description: string) {
        return (
            <div>
                <EmptyState
                    variant={EMPTY_STATE_VARIANT}
                    iconFontAwesome={OVERVIEW_EMPTY_STATE_ICON}
                    header={header}
                    description={description}
                />
                {shouldShowCreateProjectAction && (
                    <div className="mt-4 flex justify-center">
                        <CreateProjectMenu />
                    </div>
                )}
            </div>
        );
    }

    // Only the active by-project tab shows the welcome state, the other tabs have their own lists.
    const hasAnyDataForView = isStandaloneTab || isArchivedTab || hasAnyData;

    if (pageLoading) {
        return <LoadingPage />;
    }

    if (pageError) {
        return <ErrorPage error={pageError} />;
    }

    let sectionContent: React.ReactNode;
    if (isStandaloneTab && showUnassignedTableSection) {
        sectionContent = (
            <OverviewStandaloneSection
                measuringPoints={unassignedMeasuringPoints}
                loading={isUnassignedTableLoading}
                orderAndPaginationControl={orderAndPaginationControl}
            />
        );
    } else if (!hasAnyDataForView) {
        sectionContent = renderOverviewEmptyState(
            gettext('NO_PROJECTS_FOUND'),
            gettext('OVERVIEW_EMPTY_NO_PROJECTS_BODY')
        );
    } else if (!isStandaloneTab && showProjectTableSection) {
        sectionContent = (
            <OverviewCatalogSection
                title={
                    isArchivedTab
                        ? gettext('OVERVIEW_PAGE_TAB_ARCHIVED_SUBTITLE')
                        : gettext('OVERVIEW_PAGE_TAB_BY_PROJECT_SUBTITLE')
                }
                projectRows={projectRows}
                columns={columns}
                pageUpdating={pageUpdating}
                orderAndPaginationControl={orderAndPaginationControl}
                renderExpandedRows={renderExpandedRows}
                onRowClick={toggleProjectRow}
            />
        );
    } else {
        sectionContent = renderOverviewEmptyState(
            gettext('No data found'),
            isStandaloneTab
                ? gettext('OVERVIEW_EMPTY_NO_MATCHING_MEASURING_POINTS_BODY')
                : gettext('OVERVIEW_EMPTY_NO_MATCHING_PROJECTS_BODY')
        );
    }

    return (
        <BasePage title={gettext('Overview')}>
            <div className="pb-5">
                <OverviewToolbar
                    selectedTab={selectedTab}
                    onTabChange={setSelectedTab}
                    isArchivedTab={isArchivedTab}
                    onArchiveTabChange={setArchiveFilter}
                    searchTerm={searchTerm}
                    onSearchTermChange={setSearchTerm}
                    pageCount={pageCount}
                    orderAndPaginationControl={orderAndPaginationControl}
                />
                {sectionContent}
            </div>
        </BasePage>
    );
}
